import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FormController,
  FormLabel,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  Typography,
  TextField,
  Link,
} from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';
import { Formik } from 'formik';

// eslint-disable-next-line react/prop-types
const QuotesShareContent = ({ quote, onFormSubmit, formRef, onFormStateChange }) => {
  const prevFormStateRef = useRef();

  // Memoize the form state change handler to prevent unnecessary re-renders
  const handleFormStateChange = useCallback((formState) => {
    const currentStateString = JSON.stringify(formState);
    if (onFormStateChange && prevFormStateRef.current !== currentStateString) {
      prevFormStateRef.current = currentStateString;
      // Use setTimeout to defer the state update to avoid render phase warnings
      setTimeout(() => {
        onFormStateChange(formState);
      }, 0);
    }
  }, [onFormStateChange]);

  return (
    <div className="sui-flex sui-flex-col sui-gap-4" data-testid="quote-share-content">
      <Typography variant="body-base">
        Choose a Share Option and enter the email below to share your quote.
      </Typography>
      <Formik
        innerRef={formRef}
        initialValues={{ emails: [''], option: 'price-and-discounts' }}
        validate={(values) => {
          const errors = {};
          const emailErrors = [];

          values.emails.forEach((email, index) => {
            if (index === 0 && !email) {
              emailErrors[index] = 'Required';
            } else if (index > 0 && email && email.trim() === '') {
              // Additional emails that are empty spaces should be treated as invalid
              emailErrors[index] = 'Invalid email address';
            } else if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
              emailErrors[index] = 'Invalid email address';
            }
          });

          if (emailErrors.length > 0) {
            errors.emails = emailErrors;
          }

          if (!values.option) {
            errors.option = 'Please select an option';
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          const submitHandler = onFormSubmit || ((formValues, actions) => {
            setTimeout(() => {
              // Filter out empty emails
              const validEmails = formValues.emails.filter((email) => email.trim() !== '');
              // eslint-disable-next-line no-alert
              alert(JSON.stringify({ ...formValues, emails: validEmails }, null, 2));
              actions.setSubmitting(false);
            }, 400);
          });

          submitHandler(values, { setSubmitting });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => {
          // Calculate form validation state
          const hasEmailErrors = errors.emails && errors.emails.some((error) => error);
          const isFormDisabled = isSubmitting
            || !values.emails[0]
            || hasEmailErrors
            || !values.option
            || errors.option;

          // Create current form state object
          const currentFormState = {
            isSubmitting,
            values,
            errors,
            touched,
            isFormDisabled
          };

          // Use the memoized handler - it will handle the state comparison internally
          handleFormStateChange(currentFormState);

          const addEmail = () => {
            if (values.emails.length < 5) {
              setFieldValue('emails', [...values.emails, '']);
            }
          };

          const removeEmail = (index) => {
            if (values.emails.length > 1) {
              const newEmails = values.emails.filter((email, i) => i !== index);
              setFieldValue('emails', newEmails);
            }
          };

          const handleEmailChange = (index, value) => {
            const newEmails = [...values.emails];
            newEmails[index] = value;
            setFieldValue('emails', newEmails);
          };

          return (
            <form onSubmit={handleSubmit}>
              <div className="sui-flex sui-flex-col sui-gap-4">
                <FormController>
                  <FormLabel id="demo-radio-buttons-group-label">Share options</FormLabel>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    value={values.option}
                    name="option"
                    onChange={handleChange}
                  >
                    <SelectionControlLabel value="price-and-discounts" label="Price and Discounts">
                      <Radio value="price-and-discounts" />
                    </SelectionControlLabel>
                    <SelectionControlLabel value="price-only" label="Price Only">
                      <Radio value="price-only" />
                    </SelectionControlLabel>
                    <SelectionControlLabel value="no-price-or-discounts" label="No Price or Discounts">
                      <Radio value="no-price-or-discounts" />
                    </SelectionControlLabel>
                  </RadioGroup>
                  {errors.option && touched.option && (
                    <Typography variant="body-sm" color="danger">
                      {errors.option}
                    </Typography>
                  )}
                </FormController>
                {values.emails.map((email, index) => (
                  <div key={index} className="sui-relative">
                    <TextField
                      id={`email-${index}`}
                      status={errors.emails && errors.emails[index] && 'error'}
                      statusMessage={errors.emails && errors.emails[index]}
                      placeholder=""
                      onChange={(event) => handleEmailChange(index, event.target.value)}
                      value={email}
                      label={index === 0 ? 'Recipients Email' : 'Additional Recipient'}
                      fullWidth
                    />
                    {index > 0 && (
                      <div className="sui-absolute sui-top-0 sui-right-0 sui-cursor-pointer">
                        <Close
                          size="small"
                          color="danger"
                          onClick={() => removeEmail(index)}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {values.emails.length < 5 && (
                  <>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link href="#" onClick={addEmail}>
                      Add Additional Email
                    </Link>
                  </>
                )}
                <Typography variant="body-base">
                  Message (Optional)
                </Typography>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

QuotesShareContent.propTypes = {
  quote: PropTypes.shape({
    name: PropTypes.string,
    href: PropTypes.string
  }).isRequired,
  onFormSubmit: PropTypes.func,
  onFormStateChange: PropTypes.func,
  formRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({})
  ])
};

QuotesShareContent.defaultProps = {
  onFormSubmit: null,
  onFormStateChange: null,
  formRef: null
};

export default QuotesShareContent;
